import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

// Prestige-style "About" section — two-column with a large image and a
// heading/body/stats/CTA panel. Replaces the earlier numbered-chapters manifesto.

const IMAGE = "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?auto=format&fit=crop&w=1600&q=85";
const IMAGE2 = "https://images.unsplash.com/photo-1613545325268-9265e1609167?auto=format&fit=crop&w=1200&q=85";

const STATS = [
  { n: "14+", l: "Landmark projects" },
  { n: "2.8M", l: "Sq. ft. delivered" },
  { n: "6", l: "Years of quiet work" },
  { n: "100%", l: "On-time handover" },
];

export default function Manifesto() {
  return (
    <section id="philosophy" className="relative bg-editorial text-obsidian py-24 md:py-36 px-6 md:px-10 overflow-hidden">
      <div className="max-w-[1400px] mx-auto grid grid-cols-12 gap-6 md:gap-16 items-center">
        {/* Left: layered images */}
        <div className="col-span-12 md:col-span-6 relative">
          <motion.div
            initial={{ opacity: 0, y: 60 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="relative aspect-[4/5] overflow-hidden"
          >
            <img src={IMAGE} alt="KLiving craftsmanship" className="w-full h-full object-cover" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 60 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            className="hidden md:block absolute -bottom-10 -right-10 w-[52%] aspect-[4/3] overflow-hidden border-[10px] border-editorial shadow-2xl"
          >
            <img src={IMAGE2} alt="Interior detail" className="w-full h-full object-cover" />
          </motion.div>

          {/* Small accent — years badge */}
          <div className="absolute top-6 left-6 bg-obsidian text-editorial px-4 py-3 flex items-baseline gap-2">
            <span className="font-serif-display text-4xl leading-none">06</span>
            <div className="flex flex-col leading-tight">
              <span className="chapter-num text-lime_kl">YEARS</span>
              <span className="chapter-num text-editorial/60">OF CRAFT</span>
            </div>
          </div>
        </div>

        {/* Right: about text panel */}
        <div className="col-span-12 md:col-span-6">
          <div className="chapter-num text-obsidian/60 mb-6">
            <span className="inline-block w-8 h-px bg-obsidian/40 align-middle mr-3" />
            ABOUT KLIVING
          </div>

          <motion.h2
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="font-serif-display leading-[0.95] tracking-tight text-4xl md:text-5xl lg:text-6xl"
          >
            Building landmarks that <span className="italic text-emerald_kl">endure</span>, in Bhubaneswar.
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }}
            transition={{ delay: 0.1, duration: 0.8 }}
            className="mt-8 text-obsidian/75 text-lg leading-relaxed max-w-xl"
          >
            KLiving is a boutique construction and development company specialising in luxury residential, commercial and modern infrastructure projects. We combine innovative design, high-quality materials and expert craftsmanship to deliver elegant, durable and future-ready spaces.
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }}
            transition={{ delay: 0.15, duration: 0.8 }}
            className="mt-4 text-obsidian/70 text-base leading-relaxed max-w-xl"
          >
            Our focus is on excellence, transparency, timely delivery and customer satisfaction — creating properties that reflect sophistication, comfort and long-term value.
          </motion.p>

          {/* Stats grid */}
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6 border-t border-obsidian/10 pt-8">
            {STATS.map((s, i) => (
              <motion.div
                key={s.l}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ delay: 0.1 + i * 0.08, duration: 0.6 }}
              >
                <div className="font-serif-display text-4xl md:text-5xl leading-none">{s.n}</div>
                <div className="chapter-num text-obsidian/50 mt-2">{s.l.toUpperCase()}</div>
              </motion.div>
            ))}
          </div>

          <a href="#projects" className="btn-kl mt-10 bg-obsidian text-editorial border-obsidian hover:bg-emerald_kl hover:border-emerald_kl">
            Read Our Story <ArrowUpRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>
  );
}
